import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Favorite } from '@/types';

// Check if a page is favorited
export function useIsFavorited(slug: string, isAuthenticated: boolean = true) {
  return useQuery({
    queryKey: ['favorite', slug],
    queryFn: async (): Promise<boolean> => {
      const response = await api.get<ApiResponse<{ favorited: boolean }>>(`/pages/${slug}/favorited`);
      if (!response.data.success || !response.data.data) {
        return false;
      }
      return response.data.data.favorited;
    },
    enabled: !!slug && isAuthenticated,
  });
}

// Get user's favorites list
export function useFavorites(page: number = 1, perPage: number = 20) {
  return useQuery({
    queryKey: ['favorites', page, perPage],
    queryFn: async (): Promise<PaginatedData<Favorite>> => {
      const response = await api.get<ApiResponse<PaginatedData<Favorite>>>(
        `/users/me/favorites?page=${page}&perPage=${perPage}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch favorites');
      }
      return response.data.data;
    },
  });
}

// Add to favorites
export function useAddFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.post<ApiResponse<Favorite>>(`/pages/${slug}/favorite`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to add favorite');
      }
      return response.data.data;
    },
    onSuccess: (_, slug) => {
      queryClient.setQueryData(['favorite', slug], true);
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}

// Remove from favorites
export function useRemoveFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.delete<ApiResponse>(`/pages/${slug}/favorite`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to remove favorite');
      }
      return response.data.data;
    },
    onSuccess: (_, slug) => {
      queryClient.setQueryData(['favorite', slug], false);
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}

// Toggle favorite
export function useToggleFavorite() {
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  return {
    mutate: (slug: string, isFavorited: boolean) => {
      if (isFavorited) {
        removeFavorite.mutate(slug);
      } else {
        addFavorite.mutate(slug);
      }
    },
    isPending: addFavorite.isPending || removeFavorite.isPending,
  };
}
